import { useState, useEffect } from "react";
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    TextField,
    Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import * as React from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { GridColDef } from '@mui/x-data-grid';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { IGreenhouse } from "../../interfaces/IGreenhouse";
import { IBay } from "../../interfaces/IBay";
import { IZone } from "../../interfaces/IZone";
import { IBench } from "../../interfaces/IBench";
import { IItemHeader } from "../../interfaces/IItemHeader";
import { IItemDetail } from "../../interfaces/IItemDetail";
import { alpha, styled } from '@mui/material/styles';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import { setPageTitle } from "../../slices/applicationSlice";
import { IApplication } from "../../interfaces/IApplication";
import { useAppSelector, useAppDispatch } from "../../hooks";
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import { IPurchaseOrderDetail, IPurchaseOrderDetails } from "../../interfaces/PurchaseOrder/IPurchaseOrderDetail";
import { setPurchaseOrderDetailState } from "../../slices/purchaseOrderDetailSlice";
import CloseIcon from '@mui/icons-material/Close';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import EmailIcon from '@mui/icons-material/Email';
import Link from '@mui/material/Link';
import SupportIcon from '@mui/icons-material/ContactSupport';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Fab from '@mui/material/Fab';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import AddIcon from '@mui/icons-material/Add';
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";


const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
    props,
    ref,
) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});


const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
}));

export interface DialogTitleProps {
    id: string;
    children?: React.ReactNode;
    onClose: () => void;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
    const { children, onClose, ...other } = props;

    return (
        <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
            {children}
            {onClose ? (
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            ) : null}
        </DialogTitle>
    );
}

type Anchor = 'top' | 'left' | 'bottom' | 'right';

const ODD_OPACITY = 0.2;

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
    [`& .${gridClasses.row}.even`]: {
        backgroundColor: theme.palette.success.dark,
        '&:hover, &.Mui-hovered': {
            backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
            '@media (hover: none)': {
                backgroundColor: 'transparent',
            },
        },
        '&.Mui-selected': {
            backgroundColor: alpha(
                theme.palette.primary.main,
                ODD_OPACITY + theme.palette.action.selectedOpacity,
            ),
            '&:hover, &.Mui-hovered': {
                backgroundColor: alpha(
                    theme.palette.primary.main,
                    ODD_OPACITY +
                    theme.palette.action.selectedOpacity +
                    theme.palette.action.hoverOpacity,
                ),
                // Reset on touch devices, it doesn't add specificity
                '@media (hover: none)': {
                    backgroundColor: alpha(
                        theme.palette.primary.main,
                        ODD_OPACITY + theme.palette.action.selectedOpacity,
                    ),
                },
            },
        },
    },
}));



const steps = ['Phase 1', 'Phase 2'];

const darkTheme = createTheme({
    palette: {
        mode: 'dark',
        secondary: { main: '#93bd1e' },
        success: { main: 'rgba(147, 189, 30, .25)' },
        primary: { main: '#AAA' }
    },
});

interface WorkLogRow {
    description: string,
    material: string,
    size: string,
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

type Props = {
    setTabValue: React.Dispatch<React.SetStateAction<number>>,
    setAllowReview: React.Dispatch<React.SetStateAction<boolean>>,
    hideButtons: boolean,
}

export default function PoByItemDetails(props: Props) {
    const { setTabValue, setAllowReview, hideButtons } = props
    const [openSuccess, setSuccess] = React.useState(false);
    const [helpOpen, setHelpOpen] = React.useState(false);
    const [addItemOpen, setAddItemOpen] = React.useState(false);

    const handleSuccess = () => {
        setSuccess(true);
    };

    const handleSuccessClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }

        setSuccess(false);
    };

    const handleClickHelpOpen = () => {
        setHelpOpen(true);
    };

    const handleHelpClose = () => {
        setHelpOpen(false);
    };

    const handleAddItemClose = () => {
        setAddItemOpen(false);
    };

    const dispatch = useAppDispatch();

    useEffect(() => {
        let appState: IApplication = {
            pageTitle: "Purchase Order - Item Details",
            backgroundPath: "/assets/images/inventorybg.jpg"
        }
        dispatch(setPageTitle(appState));
    }, []);

    const [itemHeaders, setItemHeaders] = React.useState<IItemHeader[]>([]);
    const [itemDetails, setItemDetails] = React.useState<IItemDetail[]>([]);
    const [selectedItemDetail, setSelectedItemDetail] = React.useState<IItemDetail>();
    const [greenHouses, setGreenHouses] = React.useState<IGreenhouse[]>([]);
    const [currentGreenhouse, setCurrentGreenhouse] = React.useState<IGreenhouse>();
    const [bays, setBays] = React.useState<IBay[]>([]);
    const [currentBay, setCurrentBay] = React.useState<IBay>();
    const [zones, setZones] = React.useState<IZone[]>([]);
    const [currentZone, setCurrentZone] = React.useState<IZone>();
    const [benches, setBenches] = React.useState<IBench[]>([]);
    const [currentBench, setCurrentBench] = React.useState<IBench>();

    const [rows, setRows] = useState<IPurchaseOrderDetails>([]);

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 230, hide:true },
        { field: 'itemName', headerName: 'Name', width: 230 },
        { field: 'type', headerName: 'Item Type', width: 230 },
        { field: 'itemCost', headerName: 'Item Cost', width: 230 },
        { field: 'quantity', headerName: 'Units', width: 230 },
        { field: 'shippingType', headerName: 'Shipping Type', width: 230 },
        { field: 'surcharge', headerName: 'Surcharge', width: 230 },
        { field: 'shipDate', headerName: 'Ship Date', width: 230, valueFormatter: params =>
        moment(params?.value).format("DD/MM/YYYY hh:mm A"), },
        {
            field: 'freightOnBoard', headerName: 'FOB', width: 75, renderCell: (params) => {
                return params.value ? (
                    <CheckCircleIcon
                    />
                ) : (
                    <CloseIcon
                    />
                );
            }
        },
        { field: 'itemDescription', headerName: 'Description', width: 230 },
    ];

    useEffect(() => {
        axios
            .get(`${process.env.REACT_APP_ENDPOINT}/api/ItemHeaders`)
            .then(res => {
                console.log("Response", res);
                setItemHeaders(res.data);
            });
        axios
            .get(`${process.env.REACT_APP_ENDPOINT}/api/ItemDetails`)
            .then(res => {
                console.log("Response", res);
                setItemDetails(res.data);
            });
        axios
            .get(`${process.env.REACT_APP_ENDPOINT}/api/Greenhouses`)
            .then(res => {
                console.log("Response", res);
                setGreenHouses(res.data);
            });
        setRows(purchaseOrderDetails);
    }, []);

    let history = useNavigate();
    const Item = styled(Paper)(({ theme }) => ({
        backgroundColor: theme.palette.mode === 'dark' ? '#191919' : '#fff',
        ...theme.typography.body2,
        padding: theme.spacing(1),
        textAlign: 'center',
        color: theme.palette.text.secondary,
        border: 0,
        borderRadius: 0,
        boxShadow: "none",
        backgroundImage: "none",

    }));


    const [open, setOpen] = React.useState(false);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleAddItemOpen = () => {

    };

    interface IFormInput {
        ItemNumber: string
        Type: string
        Name: string
        Item: IItemHeader
        ItemDetail: IItemDetail,
        TraySize: number,
        Species: string,
        Variety: string,
        Color: string,
        Description: string,
        CropTime: number,
        IsTransferType: boolean,
        CustomerName: string,
        Quantity: number,
        Buffer: number,
        StartingBarcode: string,
        SowDate: string,
        SowWeek: number,
        ProductionDate: string,
        ProductionWeek: number,
        EnteredBy: string,
        AP: string,
        TransferTrayTime: number
    }

    const { register, handleSubmit, control, formState: { errors }, getValues, reset } = useForm<IPurchaseOrderDetail>();
    let purchaseOrderDetails = useAppSelector((state) => state.purchaseOrderDetails.purchaseOrderDetails);

    const onSubmit: SubmitHandler<IPurchaseOrderDetail> = data => {
        if (purchaseOrderDetails.length > 0) {
            const max = purchaseOrderDetails.reduce(function(prev, current) {
                return (prev.id > current.id) ? prev : current
            })
            data.id = max.id + 1;
        } else {
            data.id = 1;
        }
       
        dispatch(setPurchaseOrderDetailState({ purchaseOrderDetail: data, purchaseOrderDetails: [...purchaseOrderDetails, data] }))
        setRows(purchaseOrderDetails);
    };

    const [state, setState] = React.useState({
        top: false,
        left: false,
        bottom: false,
        right: false,
    });

    const toggleDrawer =
        (anchor: Anchor, open: boolean) =>
            (event: React.KeyboardEvent | React.MouseEvent) => {
                if (
                    event &&
                    event.type === 'keydown' &&
                    ((event as React.KeyboardEvent).key === 'Tab' ||
                        (event as React.KeyboardEvent).key === 'Shift')
                ) {
                    return;
                }

                setState({ ...state, [anchor]: open });
            };

    const list = (anchor: Anchor) => (
        <Box
            sx={{ width: anchor === 'top' || anchor === 'bottom' ? 'auto' : 350, height: anchor === 'top' || anchor === 'bottom' ? '50%' : 500 }}
            role="presentation"
            onClick={toggleDrawer(anchor, false)}
            onKeyDown={toggleDrawer(anchor, false)}>
            <Grid container spacing={0}>
                <Grid item xs={9}>
                    <Typography variant='h5' m={2}>Action Menu</Typography>
                </Grid>
                <Grid item xs={2}>
                    <IconButton
                        size="large"
                        aria-label="show more"
                        aria-haspopup="true"
                        onClick={handleClickHelpOpen}
                        color="inherit"
                    >
                        <SupportIcon sx={{ fontSize: '50px' }} />
                    </IconButton>
                </Grid>
            </Grid>
            <Grid container spacing={1} padding={2}>
                <Grid item xs={12}>
                    <Button startIcon={<AddCircleIcon style={{ width: '35px', height: '35px' }} />} variant="contained" color="success" fullWidth sx={{ height: '150px' }} onClick={handleAddItemOpen}>
                        <Typography variant="h5" color="#FFF" textAlign="left" fontWeight={600}>ADD ITEM</Typography>
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );

    const [orderType, setOrderType] = React.useState<string | null>('');

    const chemicalTypes = ["Acid", "Chemical", "Fertilizer"];

    const types = ['Normal', 'Chemical'];

    useEffect(() => {

    }, [orderType]);


    return (
        <div>
            <Box id="mainForm" component="form">
                <Grid container spacing={0} bgcolor='rgba(255, 255, 255, 1)'>
                    <Grid item xs={12}>
                        <Item sx={{ bgcolor: 'transparent' }}>
                            <div style={{ width: '100%'}}>
                                <StripedDataGrid
                                    rows={rows}
                                    columns={columns}
                                    pageSize={20}
                                    rowsPerPageOptions={[20]}
                                    checkboxSelection
                                    sx={{ borderColor: 'rgba(100,100,100,0.2)', minHeight: '50vh' }}
                                    getRowClassName={(params) =>
                                        params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                                    }
                                />
                            </div>
                        </Item>
                    </Grid>
                    {!hideButtons && <Grid item xs={4} sx={{ p: 1 }}>
                        <Button
                            onClick={() => { setAddItemOpen(true); }}
                            fullWidth
                            variant="contained"
                            color="primary"
                            sx={{ p: 2 }}
                            size="large">
                            <Typography variant="h5">
                                ADD NEW ITEM
                            </Typography>
                        </Button>
                    </Grid>}
                    {!hideButtons && <Grid item xs={4} sx={{ p: 1 }}>
                        <Button
                            onClick={() => { setAllowReview(true); setTabValue(0); }}
                            fullWidth
                            variant="contained"
                            color="primary"
                            sx={{ p: 2 }}
                            size="large">
                            <Typography variant="h5">
                                Go Back
                            </Typography>
                        </Button>
                    </Grid>}
                    {!hideButtons && <Grid item xs={4} sx={{ p: 1 }}>
                        <Button
                            onClick={() => { handleSubmit(onSubmit)(); setAllowReview(true); setTabValue(2); }}
                            fullWidth
                            variant="contained"
                            color="secondary"
                            sx={{ p: 2 }}
                            size="large"
                            endIcon={<CheckCircleIcon />}
                        >
                            <Typography variant="h5">
                                Review Purchase Order
                            </Typography>
                        </Button>
                    </Grid>}
                </Grid>
            </Box>

            <BootstrapDialog
                onClose={handleHelpClose}
                aria-labelledby="customized-dialog-title"
                open={helpOpen}
            >
                <BootstrapDialogTitle id="customized-dialog-title" onClose={handleHelpClose}>
                    Need Support?
                </BootstrapDialogTitle>
                <DialogContent dividers>
                    <Typography sx={{ mb: 3 }} variant="body2" gutterBottom>
                        If you are having issues or have questions, please contact us below.
                    </Typography>
                    <Button size='large' fullWidth variant="contained" startIcon={<SmartphoneIcon />} sx={{ mb: 1, bgcolor: '#89B219' }}>
                        <Link href="tel:8003141797" underline="none" color="#FFF"><Typography variant='h6' fontWeight={700}>800-314-1797</Typography></Link>
                    </Button>
                    <Button size='large' fullWidth variant="contained" startIcon={<EmailIcon />} sx={{ mb: 1, bgcolor: '#89B219' }}>
                        <Link href="mailto:bobsmarket@idatp.com" underline="none">
                            <Typography variant='h6' fontWeight={700} color="#FFF">support@idatp.com</Typography></Link>
                    </Button>
                </DialogContent>
            </BootstrapDialog>

            <BootstrapDialog
                onClose={handleAddItemClose}
                aria-labelledby="customized-dialog-title"
                open={addItemOpen}
                maxWidth='xl' fullWidth
            >
                <BootstrapDialogTitle id="customized-dialog-title" onClose={handleAddItemClose}>
                    Add Item
                </BootstrapDialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={0}>
                        <Grid item xs={12} bgcolor='rgba(255, 255, 255, 1)'>
                            <Grid container spacing={0} >
                                <Grid item xs={12}>
                                    <Grid container spacing={1} padding={3}>
                                        <Grid item xs={4}>
                                            <Item sx={{ bgcolor: 'transparent' }}>
                                                <Controller
                                                    control={control}
                                                    name="type"
                                                    render={({ field: { ref, onChange, ...field } }) => (
                                                        <Autocomplete
                                                            value={field.value}
                                                            defaultValue={types[0]}
                                                            options={types}
                                                            getOptionLabel={(option) => option}
                                                            onChange={(_, data) => {
                                                                setOrderType(data);
                                                                return onChange(data);
                                                            }}
                                                            renderInput={(params) => (
                                                                <TextField {...params} {...field} fullWidth inputRef={ref} variant="outlined" label="Type" sx={{ fontSize: '24px', fontWeight: '900' }}></TextField>
                                                            )}
                                                        />
                                                    )}
                                                />
                                            </Item>
                                        </Grid>
                                        <Grid item xs={12}><Divider /></Grid>
                                        <Grid item xs={4}>
                                            <Item sx={{ bgcolor: 'transparent' }}><TextField required variant="outlined" fullWidth id="itemname" label="Item Name" {...register("itemName")} /></Item>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Item sx={{ bgcolor: 'transparent' }}><TextField required variant="outlined" fullWidth id="itemcost" label="Item Cost" {...register("itemCost")} /></Item>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Item sx={{ bgcolor: 'transparent' }}><TextField required variant="outlined" fullWidth id="itemdescription" label="Item Description" {...register("itemDescription")} /></Item>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Item sx={{ bgcolor: 'transparent' }}><TextField required variant="outlined" fullWidth id="quantity" label="Quantity"  {...register("quantity")} /></Item>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Item sx={{ bgcolor: 'transparent' }}><TextField required variant="outlined" fullWidth id="shippingtype" label="Shipping Type" {...register("shippingType")} /></Item>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Item sx={{ bgcolor: 'transparent' }}>
                                                <FormControlLabel control={<Checkbox defaultChecked sx={{ '& .MuiSvgIcon-root': { fontSize: 40 } }} />} label="FOB" {...register('freightOnBoard')} />
                                            </Item>
                                        </Grid>
                                        <Grid item xs={4} >
                                            <Item sx={{ bgcolor: 'transparent' }}>
                                                <Controller
                                                    {...register('shipDate', { valueAsDate: true })}
                                                    control={control}
                                                    render={
                                                        ({ field: { onChange, ...restField } }) =>
                                                            <DatePicker
                                                                label="Ship Date"
                                                                onChange={(event) => { onChange(event); }}
                                                                renderInput={(params) =>
                                                                    <TextField
                                                                        fullWidth
                                                                        variant="outlined"
                                                                        {...params}
                                                                    />}
                                                                {...restField}
                                                            />
                                                    }
                                                />
                                            </Item>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Item sx={{ bgcolor: 'transparent' }}><TextField required variant="outlined" fullWidth id="surcharge" label="Surcharge" {...register('surcharge')} /></Item>
                                        </Grid>
                                        {orderType == 'Chemical' &&
                                            <Grid item xs={4}>
                                                <Item sx={{ bgcolor: 'transparent' }}><TextField required variant="outlined" fullWidth id="epanumber" label="EPA Number" {...register('epaNumber')} /></Item>
                                            </Grid>
                                        }

                                        {orderType == 'Chemical' &&
                                            <Grid item xs={4} >
                                                <Item sx={{ bgcolor: 'transparent' }}><TextField required variant="outlined" fullWidth id="activeingredient" label="Active Ingedient" {...register('activeIngrediant')} /></Item>
                                            </Grid>
                                        }

                                        {orderType == 'Chemical' &&
                                            <Grid item xs={4}>
                                                <Item sx={{ bgcolor: 'transparent' }}><TextField required variant="outlined" fullWidth id="rei" label="REI" {...register("rei")} /></Item>
                                            </Grid>}

                                        {orderType == 'Chemical' &&
                                            <Grid item xs={4}>
                                                <Item sx={{ bgcolor: 'transparent' }}><Controller
                                                    control={control}
                                                    name="chemicalType"
                                                    render={({ field: { ref, onChange, ...field } }) => (
                                                        <Autocomplete
                                                            options={chemicalTypes}
                                                            getOptionLabel={(option) => option}
                                                            onChange={(_, data) => {
                                                                return onChange(data);
                                                            }}
                                                            renderInput={(params) => (
                                                                <TextField {...params} {...field} fullWidth inputRef={ref} variant="outlined" label="Chemical Type" sx={{ fontSize: '24px', fontWeight: '900' }}></TextField>
                                                            )}
                                                        />
                                                    )}
                                                /></Item>
                                            </Grid>
                                        }
                                        <Grid item xs={6}>
                                            <Button
                                                fullWidth
                                                onClick={() => { setAddItemOpen(false); }}
                                                variant="contained"
                                                color="primary"
                                                sx={{ p: 2 }}
                                                size="large"
                                                endIcon={<CheckCircleIcon />}
                                            >
                                                <Typography variant="h5">
                                                    CLOSE
                                                </Typography>
                                            </Button>
                                        </Grid>

                                        <Grid item xs={6} >
                                            <Button
                                                fullWidth
                                                onClick={() => { handleSubmit(onSubmit)(); setAllowReview(true); setRows(purchaseOrderDetails); }}
                                                variant="contained"
                                                color="secondary"
                                                sx={{ p: 2 }}
                                                size="large"
                                                endIcon={<CheckCircleIcon />}
                                            >
                                                <Typography variant="h5">
                                                    ADD ITEM
                                                </Typography>
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </DialogContent>
            </BootstrapDialog>

        </div>
    );
}